// R2 URL transformation utility for admin dashboard
const R2_PUBLIC_BASE_URL = 'https://pub-b8052675ae6444aaa77c31ae204cbd7f.r2.dev';

/**
 * Transform R2 path to public URL
 * @param r2Path - Path in format "r2://bucket-name/path/to/file.ext"
 * @returns Public URL or null if path is invalid
 */
export function transformR2PathToPublicUrl(r2Path: string | null | undefined): string | null {
  if (!r2Path || typeof r2Path !== 'string') {
    return null;
  }

  // Handle r2:// protocol paths
  if (r2Path.startsWith('r2://')) {
    // Extract the path after the bucket name
    // r2://obta-historical-documents/thumbnails/5-thumb.jpg -> /thumbnails/5-thumb.jpg
    const pathParts = r2Path.replace('r2://', '').split('/');
    if (pathParts.length < 2) {
      return null;
    }
    
    // Remove bucket name and join the rest
    const filePath = pathParts.slice(1).join('/');
    return `${R2_PUBLIC_BASE_URL}/${filePath}`;
  }

  // Handle paths that are already HTTP URLs
  if (r2Path.startsWith('http://') || r2Path.startsWith('https://')) {
    return r2Path;
  }

  // Handle relative paths (assume they're relative to the R2 bucket)
  if (r2Path.startsWith('/')) {
    return `${R2_PUBLIC_BASE_URL}${r2Path}`;
  }

  // Handle paths without leading slash
  return `${R2_PUBLIC_BASE_URL}/${r2Path}`;
}

/**
 * Get image URL with fallback handling
 * @param thumbnailPath - Preferred thumbnail path
 * @param storagePath - Fallback storage path
 * @returns Public URL or null
 */
export function getImageUrl(thumbnailPath: string | null, storagePath: string): string | null {
  // Try thumbnail first
  if (thumbnailPath) {
    const thumbnailUrl = transformR2PathToPublicUrl(thumbnailPath);
    if (thumbnailUrl) {
      return thumbnailUrl;
    }
  }

  // Fall back to storage path for images
  const storageUrl = transformR2PathToPublicUrl(storagePath);
  if (storageUrl && isImageFile(storagePath)) {
    return storageUrl;
  }

  return null;
}

/**
 * Check if file is an image based on extension
 * @param filePath - File path or name
 * @returns True if file appears to be an image
 */
export function isImageFile(filePath: string): boolean {
  if (!filePath) return false;
  
  const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.tiff', '.tif', '.bmp'];
  const extension = filePath.toLowerCase().split('.').pop();
  
  return extension ? imageExtensions.includes(`.${extension}`) : false;
}

/**
 * Check if file is a PDF based on extension
 * @param filePath - File path or name
 * @returns True if file appears to be a PDF
 */
export function isPdfFile(filePath: string): boolean {
  if (!filePath) return false;
  return filePath.toLowerCase().endsWith('.pdf');
}
